import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import style from "./style.module.css";
import { HeaderSide } from "./headerside";
import { MapWithPoligons } from "../../MapWithPoligons";
import L from "leaflet";
import { EntAnaliticalDataHeatAnimalsFilter } from "./api/types";
import { Select } from "antd";
import {
  useGetAnaliticalDataHeatAnimalSummarysListQuery,
  useGetAnaliticalDataHeatAnimalRepublicsListQuery,
  useGetAnaliticalDataHeatAnimalByRegionsListQuery,
} from "./api";
import { PreloaderSvg } from "../../preloaderSvg/PreloaderSvg";
import { MainTable } from "./mainTable/mainTable";
import { LeftGraph } from "./leftGraph";
import { RegionTable } from "./regionTable/regionTable";
import SelectTerritorialUnitsCode from "../../selects/SelectTerritorialUnitsCode";
import  SelectAnimalType from "../../selects/SelectAnimalType";
import { Button } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";

export const Heatmapbytypesofanimals = () => {
  const { t } = useTranslation();

  const [filters, setFilters] = useState<EntAnaliticalDataHeatAnimalsFilter>({
    year: 2024,
    page: 1,
    size: 1000,
    regionId: 2067,
  });

  const [showMainTable, setShowMainTable] = useState(false);
  const [showRegionTable, setShowRegionTable] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  const [showAllDistricts, setShowAllDistricts] = useState(false);
  
  const regionsFilter: EntAnaliticalDataHeatAnimalsFilter = {
    year: filters.year,
    page: 1,
    size: 1000,
    animalTypeIds: filters.animalTypeIds
  };

  const byRegionFilter: EntAnaliticalDataHeatAnimalsFilter = {
    year: filters.year,
    page: 1,
    size: 1000,
    regionId: showAllDistricts ? undefined : (selectedRegionId || filters.regionId || 2067),
    animalTypeIds: filters.animalTypeIds
  };

  const { data: summaryData, isLoading: summaryLoading } =
    useGetAnaliticalDataHeatAnimalSummarysListQuery(filters);

  const { data: republicData, isLoading: republicLoading } =
    useGetAnaliticalDataHeatAnimalRepublicsListQuery(filters);
  
  const { data: byRegionData, isLoading: byRegionLoading } =
    useGetAnaliticalDataHeatAnimalByRegionsListQuery(byRegionFilter);

  const handleYearChange = (year: number | null) => {
    setFilters((prev) => ({
      ...prev,
      year: year || new Date().getFullYear(),
      page: 1,
    }));
  };
  const handleAnimalTypeChange = (animalTypeId: number | null) => {
    setFilters((prev) => ({
      ...prev,
      animalTypeIds: animalTypeId ? [animalTypeId] : undefined,
      page: 1,
    }));
  };
  const handleRegionChange = (regionId: string | number, option?: any) => {
    const numericRegionId = typeof regionId === 'string' ? parseInt(regionId, 10) : regionId;
    

    if (numericRegionId === 0) {
      setFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters.regionId;
        return { ...newFilters, page: 1 };
      });
      setSelectedRegionId(null);
    } else {
      setFilters((prev) => ({
        ...prev,
        regionId: numericRegionId,
        page: 1,
      }));
      setSelectedRegionId(null);
    }
  };

  const handlePolygonClick = (regionId: number) => {
    // Если включен режим показа всех районов, можно кликать только на области
    if (showAllDistricts) {
      // Проверяем, что кликнули на область (REGION), а не на район
      const clickedRegion = republicData?.res?.data?.find((r: any) => r.id === regionId);
      if (clickedRegion) {
        setSelectedRegionId(regionId);
        setShowAllDistricts(false);
      }
      return;
    }
    
    // Если кликнули на область (REGION), показываем районы этой области
    if (!selectedRegionId) {
      setSelectedRegionId(regionId);
    } else if (selectedRegionId !== regionId) {
      // Если кликнули на другую область, обновляем selectedRegionId
      setSelectedRegionId(regionId);
    } else {
      // Если кликнули на район той же области, обновляем фильтры
      setFilters((prev) => ({
        ...prev,
        regionId,
        page: 1,
      }));
    }
  };

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const customMarkerIcon = L.icon({
    iconUrl: "/import.svg",
    iconSize: [35, 55],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  const getColorByAmount = (amount: number, maxAmount: number) => {
    if (amount === 0) return '#e0f7e9'; 
    const percentage = (amount / maxAmount) * 100;
    if (percentage >= 80) return '#1b5e20'; 
    if (percentage >= 60) return '#388e3c'; 
    if (percentage >= 40) return '#66bb6a'; 
    if (percentage >= 20) return '#a5d6a7'; 
    return '#c8e6c9'; 
  };

  const mapData = (() => {
    if (showAllDistricts && selectedRegionId) {
      // 🔥 Только районы выбранной области — цветные
      const maxAmount = Math.max(...(byRegionData?.res?.data?.map((r: any) => r.amount) || [0]));
  
      return byRegionData?.res?.data?.map((region: any) => {
        const isInSelectedRegion = region.parentId === selectedRegionId;
  
        return {
          regionId: region.id,
          nameRu: region.nameRu,
          nameKy: region.nameKy,
          amount: region.amount,
          color: isInSelectedRegion
            ? getColorByAmount(region.amount, maxAmount)
            : 'rgba(255, 255, 255, 0)', // прозрачный
        };
      }) || [];
    }
  
    if (showAllDistricts) {
      // 🔥 Все районы (по всей республике)
      const maxAmount = Math.max(...(byRegionData?.res?.data?.map((r: any) => r.amount) || [0]));
  
      return byRegionData?.res?.data?.map((region: any) => ({
        regionId: region.id,
        nameRu: region.nameRu,
        nameKy: region.nameKy,
        amount: region.amount,
        color: getColorByAmount(region.amount, maxAmount),
      })) || [];
    }
  
    if (selectedRegionId) {
      // 🔥 Только выбранная область, остальные районы прозрачные
      const maxAmount = Math.max(...(byRegionData?.res?.data?.map((r: any) => r.amount) || [0]));
  
      return byRegionData?.res?.data?.map((region: any) => ({
        regionId: region.id,
        nameRu: region.nameRu,
        nameKy: region.nameKy,
        amount: region.amount,
        color: getColorByAmount(region.amount, maxAmount),
      })) || [];
    }
  
    // 🔥 Карта по республике
    const maxAmount = Math.max(...(republicData?.res?.data?.map((r: any) => r.amount) || [0]));
  
    return republicData?.res?.data?.map((region: any) => ({
      regionId: region.id,
      nameRu: region.nameRu,
      nameKy: region.nameKy,
      amount: region.amount,
      color: getColorByAmount(region.amount, maxAmount),
    })) || [];
  })();
  

  const mapPolygonTypes = ["REGION", "AREA", "CITY_OF_REPUBLICAN_SIGNIFICANCE"]; 

  const selectedRegionName = (() => {
    if (!filters.regionId || !republicData?.res?.data) return '';
    const region = republicData.res.data.find((r: any) => r.id === filters.regionId);
    return region ? region.nameRu : '';
  })();

  return (
    <div className={style.container}>
      <HeaderSide />
      
      <div style={{
        display: 'flex',
        gap: 16,
        marginBottom: 20,
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        <Button
          type={showMainTable ? "default" : "primary"}
          icon={showMainTable ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          shape="round"
          size="middle"
          style={{
            boxShadow: showMainTable ? undefined : "0 2px 8px rgba(24, 144, 255, 0.15)",
            transition: "all 0.2s"
          }}
          onClick={() => setShowMainTable((v) => !v)}
        >
          {showMainTable ? "Скрыть таблицу по республике" : "Показать таблицу по республике"}
        </Button>
        <Button
          type={showRegionTable ? "default" : "primary"}
          icon={showRegionTable ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          shape="round"
          size="middle"
          style={{
            boxShadow: showRegionTable ? undefined : "0 2px 8px rgba(24, 144, 255, 0.15)",
            transition: "all 0.2s"
          }}
          onClick={() => setShowRegionTable((v) => !v)}
        >
          {showRegionTable ? "Скрыть таблицу по регионам" : "Показать таблицу по регионам"}
        </Button>
        {selectedRegionId && !showAllDistricts && (
          <Button
            type="default"
            shape="round"
            size="middle"
            onClick={() => {
              setSelectedRegionId(null);
              setFilters((prev) => ({
                ...prev,
                regionId: 2067,
                page: 1,
              }));
            }}
          >
            Вернуться к областям
          </Button>
        )}
      </div>
      <div className={style.mapFilters}>
      <Select
                style={{ width: 120 }}
                value={filters.year}
                onChange={handleYearChange}
                placeholder={t("Выберите год")}
              >
                {years.map((year) => (
                  <Select.Option key={year} value={year}>
                    {year}
                  </Select.Option>
                ))}
              </Select>
              <div style={{ width: 120 }}>
                <SelectTerritorialUnitsCode
                  type={["REGION", "CITY_OF_REPUBLICAN_SIGNIFICANCE"]}
                  value={filters.regionId}
                  onChange={handleRegionChange}
                />
              </div>
              <div style={{ width: 120 }}>
                <SelectAnimalType
                  value={filters.animalTypeIds?.[0]}
                  onChange={handleAnimalTypeChange}
                />
              </div>
              </div>
      <div className={style.mapContainer}>
        {showMainTable && (
          <div className={style.tableSection}>
            <MainTable data={republicData} isLoading={republicLoading} />
          </div>
        )}
        <div className={style.mapSection} style={{ width: (!showMainTable && !showRegionTable) ? '100%' : undefined, flexGrow: 1 }}>
          <div className={style.mapContent}>
            <div className={style.mapContent}>
              <MapWithPoligons
                latitude={41.5089324}
                longitude={74.724091}
                customIcon={customMarkerIcon}
                type={mapPolygonTypes}
                selectedYear={filters.year}
                extraData={mapData}
                onPolygonClick={handlePolygonClick}
                selectedRegionId={selectedRegionId}
                showAllDistricts={showAllDistricts}
              />
            </div>
          </div>
          
        
        </div>
        {showRegionTable && (
          <div className={style.regionTableSection}>
            <RegionTable data={byRegionData} isLoading={byRegionLoading} selectedRegionName={selectedRegionName} />
          </div>
        )}
      </div>

      {(summaryLoading || republicLoading || byRegionLoading) && <PreloaderSvg />}

      <div className={style.chartSection}>
            <LeftGraph data={summaryData} isLoading={summaryLoading} />
          </div>
    </div>
  );
};
